import { UploadedFile } from "components/InputMultipleFiles";
import { useFormik } from "formik";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileService } from "services/fileService";
import { formatDate } from "utils/formatDate";
import * as Yup from "yup";

const validationSchema = Yup.object({
  title: Yup.string()
    .required("Título é obrigatório")
    .min(5, "Título deve ter pelo menos 5 caracteres"),
  description: Yup.string().optional(),
  startTime: Yup.string().required("Data/Hora de início é obrigatória"),
  durationMinutes: Yup.number()
    .required("Duração é obrigatória")
    .min(1, "Duração deve ser maior que 0")
    .max(1440, "Duração não pode ser maior que 24 horas")
    .integer("Duração deve ser um número inteiro"),
  location: Yup.string().optional(),
  createPublicChannel: Yup.boolean().optional(),
  channelName: Yup.string().when("createPublicChannel", {
    is: true,
    then: (schema) =>
      schema.required(
        "Nome do canal é obrigatório quando canal público está marcado"
      ),
    otherwise: (schema) => schema.optional(),
  }),
  channelDescription: Yup.string().optional(),
  numberOfSeats: Yup.number()
    .positive("O número de assentos deve ser maior que zero")
    .nullable()
    .optional(),
  videoLink: Yup.string()
    .url("URL inválida")
    .test("is-youtube", "Apenas links do YouTube são permitidos", (value) => {
      if (!value || value.trim() === "") return true; // Allow empty values
      const youtubeRegex =
        /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/i;
      return youtubeRegex.test(value);
    })
    .optional()
    .nullable(),
});

const initInputsValues = {
  title: "",
  description: "",
  startTime: "",
  durationMinutes: 60,
  location: "",
  trackIds: [] as string[],
  speakerIds: [] as string[],
  createPublicChannel: false,
  channelName: "",
  channelDescription: "",
  allowPublicView: false,
  transcript: "",
  type: "",
  numberOfSeats: "",
  availableSeats: "",
  videoLink: "",
};

export const useSessionForm = (
  onSuccess?: () => void,
  onGenerateQuestionnaire?: (questionnaire: any) => void,
  editingSession?: any,
  providedEventId?: string
) => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();

  const [initialInputsValues, setInitialInputsValues] =
    useState(initInputsValues);
  const [isLoading, setIsLoading] = useState(false);
  const [tracks, setTracks] = useState<
    Array<{ trackId: string; name: string }>
  >([]);
  const [speakers, setSpeakers] = useState<
    Array<{ registrationId: string; profile: { name: string } }>
  >([]);
  const [locations, setLocations] = useState<
    Array<{ locationId: string; name: string }>
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [hasExistingConversation, setHasExistingConversation] = useState(false);
  const [checkingConversation, setCheckingConversation] = useState(false);
  const [existingConversationData, setExistingConversationData] = useState<{
    conversationId: string;
    name: string;
    description: string;
    allowPublicView: boolean;
  } | null>(null);

  // Questionnaire-related states
  const [transcriptFile, setTranscriptFile] = useState<File | null>(null);
  const [generatingQuestionnaire, setGeneratingQuestionnaire] = useState(false);
  const [publishingQuestionnaire, setPublishingQuestionnaire] = useState(false);

  // Multiple files state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  const isEditing = !!editingSession;

  // Initialize form values based on editing mode
  useEffect(() => {
    if (editingSession) {
      // Format startTime for datetime-local input
      const startTimeFormatted = editingSession.startTime
        ? formatDate(new Date(editingSession.startTime))
        : "";

      const editValues = {
        title: editingSession.title || "",
        description: editingSession.description || "",
        startTime: startTimeFormatted,
        durationMinutes: editingSession.durationMinutes || 60,
        location:
          editingSession.location?.locationId || editingSession.location || "",
        trackIds:
          editingSession.tracks?.map((track: any) => track.trackId) || [],
        speakerIds:
          editingSession.speakers?.map(
            (speaker: any) => speaker.registrationId
          ) || [],
        createPublicChannel: false, // Default to false for editing mode
        channelName: existingConversationData?.name || "",
        channelDescription: existingConversationData?.description || "",
        allowPublicView: existingConversationData?.allowPublicView || false,
        transcript: editingSession.transcript || "",
        type: editingSession.type || "",
        numberOfSeats: editingSession.numberOfSeats
          ? editingSession.numberOfSeats.toString()
          : "",
        availableSeats:
          editingSession.availableSeats !== undefined &&
          editingSession.availableSeats !== null
            ? editingSession.availableSeats.toString()
            : "",
        videoLink: editingSession.videoLink || "",
      };

      setInitialInputsValues(editValues);
      formik.setValues(editValues);

      // Clear previous files first
      setUploadedFiles([]);
      setFilesToUpload([]);

      // Load existing presentationFiles if editing session has presentationFiles
      if (
        editingSession.presentationFiles &&
        Array.isArray(editingSession.presentationFiles)
      ) {
        const existingFiles: UploadedFile[] =
          editingSession.presentationFiles.map((file: any, index: number) => {
            const filename =
              file.originalFilename || file.filename || `Arquivo ${index + 1}`;

            // Detect image based on mimeType that comes as extension (jpg, png, etc)
            const imageExtensions = [
              "jpg",
              "jpeg",
              "png",
              "gif",
              "bmp",
              "webp",
              "svg",
            ];
            const isImage =
              file.mimeType &&
              imageExtensions.includes(file.mimeType.toLowerCase());

            return {
              id: file.fileId,
              file: null, // Existing files don't have File object
              name: filename,
              size: "Arquivo existente",
              previewUrl:
                isImage && file.signedFileUrl ? file.signedFileUrl : undefined,
            };
          });

        setUploadedFiles(existingFiles);
      }
    } else {
      setInitialInputsValues(initInputsValues);
      formik.setValues(initInputsValues);
      setUploadedFiles([]);
      setFilesToUpload([]);
    }
  }, [editingSession]);

  // Load tracks and speakers when component mounts or eventId changes
  useEffect(() => {
    if (providedEventId) {
      loadTracksAndSpeakersAndLocations();
    }
  }, [providedEventId]);

  // Check if conversation exists for editing session
  useEffect(() => {
    if (isEditing && editingSession?.sessionId) {
      checkExistingConversation();
    }
  }, [isEditing, editingSession?.sessionId]);

  // Update form values when existing conversation data changes
  useEffect(() => {
    if (isEditing && existingConversationData) {
      formik.setFieldValue("channelName", existingConversationData.name);
      formik.setFieldValue(
        "channelDescription",
        existingConversationData.description
      );
      formik.setFieldValue(
        "allowPublicView",
        existingConversationData.allowPublicView
      );
    }
  }, [existingConversationData, isEditing]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        if (file.previewUrl && file.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, []);

  const checkExistingConversation = async () => {
    if (!editingSession?.sessionId) return;

    setCheckingConversation(true);

    try {
      const query = `
        query BackofficeSessionConversation($eventId: ID!, $sessionId: ID!) {
          backofficeSessionConversation(eventId: $eventId, sessionId: $sessionId) {
            conversationId
            name
            description
            allowPublicView
          }
        }
      `;

      const response = await connectGraphQLApi({
        query,
        variables: {
          eventId: providedEventId,
          sessionId: editingSession.sessionId,
        },
        errorMessage: "Falha ao verificar conversação da sessão",
        useNewApi: true,
      });

      const existingConversation = response.data.backofficeSessionConversation;
      setHasExistingConversation(!!existingConversation);

      if (existingConversation) {
        setExistingConversationData({
          conversationId: existingConversation.conversationId,
          name: existingConversation.name || "",
          description: existingConversation.description || "",
          allowPublicView: existingConversation.allowPublicView || false,
        });
      } else {
        setExistingConversationData(null);
      }
    } catch (error) {
      console.error("Error checking existing conversation:", error);
      // If error checking, assume no conversation exists to allow creating one
      setHasExistingConversation(false);
    } finally {
      setCheckingConversation(false);
    }
  };

  const loadTracksAndSpeakersAndLocations = async () => {
    if (!providedEventId) return;

    setLoadingOptions(true);

    try {
      // Load tracks
      const tracksQuery = `
        query BackofficeTracks($filter: TrackFilterInput!) {
          backofficeTracks(filter: $filter) {
            trackId
            name
            color
            description
          }
        }
      `;

      // Load speakers (registrations with isSpeaker = true)
      const speakersQuery = `
        query BackofficeRegistrationsEvent($eventId: ID!, $filter: BackofficeRegistrationFilterInput) {
          backofficeRegistrationsEvent(eventId: $eventId, filter: $filter) {
            registrationId
            profile {
              name
              company
              position
              isSpeaker
            }
            customer {
              name
            }
          }
        }
      `;

      // Load locations (only active ones)
      const locationsQuery = `
        query BackofficeLocations($filter: LocationFilterInput!) {
          backofficeLocations(filter: $filter) {
            locationId
            name
            description
            isActive
          }
        }
      `;

      const [tracksResponse, speakersResponse, locationsResponse] =
        await Promise.all([
          connectGraphQLApi({
            query: tracksQuery,
            variables: { filter: { eventId: providedEventId } },
            errorMessage: "Falha ao carregar tracks",
            useNewApi: true,
          }),
          connectGraphQLApi({
            query: speakersQuery,
            variables: {
              eventId: providedEventId,
              filter: { isSpeaker: true },
            },
            errorMessage: "Falha ao carregar speakers",
            useNewApi: true,
          }),
          connectGraphQLApi({
            query: locationsQuery,
            variables: {
              filter: {
                eventId: providedEventId,
                isActive: true,
              },
            },
            errorMessage: "Falha ao carregar locais",
            useNewApi: true,
          }),
        ]);

      if (tracksResponse.data) {
        setTracks(tracksResponse.data.backofficeTracks || []);
      }

      if (speakersResponse.data) {
        // Backend already filters speakers
        setSpeakers(speakersResponse.data.backofficeRegistrationsEvent || []);
      }

      if (locationsResponse.data) {
        setLocations(locationsResponse.data.backofficeLocations || []);
      }
    } catch (error) {
      console.error("Error loading tracks, speakers and locations:", error);
      setToast({
        type: "error",
        message: "Erro ao carregar tracks, speakers e locais",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } finally {
      setLoadingOptions(false);
    }
  };

  const sendData = async (values: any) => {
    setIsLoading(true);

    try {
      // Build input object dynamically, only including non-empty optional fields
      const input: any = {
        title: values.title.trim(),
        startTime: new Date(values.startTime).toISOString(),
        durationMinutes: parseInt(values.durationMinutes.toString()),
      };

      // Only include optional fields if they have values
      if (values.description && values.description.trim()) {
        input.description = values.description.trim();
      }

      if (values.location && values.location.trim()) {
        input.locationId = values.location.trim();
      }

      // Always include trackIds and speakerIds (even if empty arrays)
      input.trackIds = values?.trackIds || [];
      input.speakerIds = values?.speakerIds || [];

      // Include transcript if it has content
      if (values.transcript && values.transcript.trim()) {
        input.transcript = values.transcript.trim();
      }

      // Include type if it has a value
      if (values.type && values.type.trim()) {
        input.type = values.type.trim();
      }

      // Include numberOfSeats if it has a value
      if (values.numberOfSeats && values.numberOfSeats.toString().trim()) {
        input.numberOfSeats = parseInt(values.numberOfSeats.toString());
      } else {
        input.numberOfSeats = null;
      }

      // Include videoLink if it has a value (only for updates)
      if (
        isEditing &&
        values.videoLink !== undefined &&
        values.videoLink !== null
      ) {
        // Send the trimmed value, or empty string to clear it
        input.videoLink = values.videoLink.trim();
      }

      if (isEditing) {
        // Update existing Session
        await connectGraphQLApi({
          query: `
            mutation BackofficeUpdateSession($sessionId: ID!, $input: SessionUpdateInput!, $lastUpdated: String!) {
              backofficeUpdateSession(sessionId: $sessionId, input: $input, lastUpdated: $lastUpdated) {
                sessionId
                title
                description
                startTime
                durationMinutes
                location {
                  locationId
                  name
                }
                type
                numberOfSeats
                availableSeats
                transcript
                videoLink
              }
            }
          `,
          variables: {
            sessionId: editingSession.sessionId,
            input,
            lastUpdated: editingSession.updateDate.toString(),
          },
          errorMessage: "Falha ao atualizar sessão",
          useNewApi: true,
        });

        // Upload multiple files if provided
        if (filesToUpload.length > 0) {
          const sessionId = editingSession.sessionId;

          for (const file of filesToUpload) {
            await FileService.uploadFile({
              file,
              refCollectionId: sessionId,
              refCollectionName: "sessions",
              refCollectionField: "presentationFileIds",
              refCollectionFieldType: "array",
            });
          }
        }

        // Handle conversation creation or update
        if (values.createPublicChannel && !hasExistingConversation) {
          // Create new conversation
          try {
            await connectGraphQLApi({
              query: `
                mutation BackofficeCreateConversation($input: BackofficeConversationInput!) {
                  backofficeCreateConversation(input: $input) {
                    conversationId
                    name
                    description
                    conversationType
                    allowPublicView
                  }
                }
              `,
              variables: {
                input: {
                  eventId: providedEventId,
                  sessionId: editingSession.sessionId,
                  name: values.channelName.trim() || values.title.trim(),
                  description:
                    values.channelDescription.trim() ||
                    values.description.trim(),
                  conversationType: "GROUP",
                  allowPublicView: values.allowPublicView || false,
                },
              },
              errorMessage: "Falha ao criar canal público da sessão",
              useNewApi: true,
            });

            setToast({
              type: "success",
              message: "Sessão atualizada e canal público criado com sucesso!",
              isActive: true,
              timeToRemoveToast: 3000,
            });
          } catch (conversationError) {
            console.error("Error creating conversation:", conversationError);
            setToast({
              type: "warning",
              message:
                "Sessão atualizada, mas houve erro ao criar o canal público.",
              isActive: true,
              timeToRemoveToast: 5000,
            });
          }
        } else if (hasExistingConversation && existingConversationData) {
          // Update existing conversation
          try {
            await connectGraphQLApi({
              query: `
                mutation BackofficeUpdateConversation($conversationId: ID!, $input: BackofficeConversationUpdateInput!) {
                  backofficeUpdateConversation(conversationId: $conversationId, input: $input) {
                    conversationId
                    name
                    description
                    conversationType
                    allowPublicView
                  }
                }
              `,
              variables: {
                conversationId: existingConversationData.conversationId,
                input: {
                  name: values.channelName.trim() || values.title.trim(),
                  description:
                    values.channelDescription.trim() ||
                    values.description.trim(),
                  allowPublicView: values.allowPublicView || false,
                },
              },
              errorMessage: "Falha ao atualizar canal público da sessão",
              useNewApi: true,
            });

            setToast({
              type: "success",
              message: "Sessão e canal público atualizados com sucesso!",
              isActive: true,
              timeToRemoveToast: 3000,
            });
          } catch (conversationError) {
            console.error("Error updating conversation:", conversationError);
            setToast({
              type: "warning",
              message:
                "Sessão atualizada, mas houve erro ao atualizar o canal público.",
              isActive: true,
              timeToRemoveToast: 5000,
            });
          }
        } else {
          setToast({
            type: "success",
            message: "Sessão atualizada com sucesso!",
            isActive: true,
            timeToRemoveToast: 3000,
          });
        }
      } else {
        // Create new Session - include eventId for creation
        const createInput = {
          ...input,
          eventId: providedEventId,
        };

        const sessionResponse = await connectGraphQLApi({
          query: `
            mutation BackofficeCreateSession($input: SessionInput!) {
              backofficeCreateSession(input: $input) {
                sessionId
                title
                description
                startTime
                durationMinutes
                location {
                  locationId
                  name
                }
                type
                numberOfSeats
                availableSeats
              }
            }
          `,
          variables: { input: createInput },
          errorMessage: "Falha ao criar sessão",
          useNewApi: true,
        });

        const createdSession = sessionResponse.data.backofficeCreateSession;

        // Upload multiple files if provided
        if (filesToUpload.length > 0 && createdSession) {
          const sessionId = createdSession.sessionId;

          for (const file of filesToUpload) {
            await FileService.uploadFile({
              file,
              refCollectionId: sessionId,
              refCollectionName: "sessions",
              refCollectionField: "presentationFileIds",
              refCollectionFieldType: "array",
            });
          }
        }

        // Create public channel if checkbox is checked
        if (values.createPublicChannel && createdSession) {
          try {
            await connectGraphQLApi({
              query: `
                mutation BackofficeCreateConversation($input: BackofficeConversationInput!) {
                  backofficeCreateConversation(input: $input) {
                    conversationId
                    name
                    description
                    conversationType
                    allowPublicView
                  }
                }
              `,
              variables: {
                input: {
                  eventId: providedEventId,
                  sessionId: createdSession.sessionId,
                  name: values.channelName.trim() || values.title.trim(),
                  description:
                    values.channelDescription.trim() ||
                    values.description.trim(),
                  conversationType: "GROUP",
                  groupSettings: {
                    onlyAdminsCanSend: false,
                  },
                  isPinned: false,
                  allowPublicView: values.allowPublicView || false,
                },
              },
              errorMessage: "Falha ao criar canal público da sessão",
              useNewApi: true,
            });

            setToast({
              type: "success",
              message: "Sessão e canal público criados com sucesso!",
              isActive: true,
              timeToRemoveToast: 3000,
            });
          } catch (conversationError) {
            console.error("Error creating conversation:", conversationError);
            setToast({
              type: "warning",
              message:
                "Sessão criada, mas houve erro ao criar o canal público.",
              isActive: true,
              timeToRemoveToast: 5000,
            });
          }
        } else {
          setToast({
            type: "success",
            message: "Sessão criada com sucesso!",
            isActive: true,
            timeToRemoveToast: 3000,
          });
        }
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const message =
        error?.message ||
        `Erro ao ${isEditing ? "atualizar" : "criar"} sessão. Tente novamente.`;

      console.error("Error saving session:", error);
      setToast({
        type: "error",
        message,
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: initialInputsValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: sendData,
  });

  // Keep a stable reference to formik for callbacks
  const formikRef = useRef(formik);
  formikRef.current = formik;

  // Pre-fill channel fields when checkbox is checked in creation mode
  useEffect(() => {
    if (!isEditing && formik.values.createPublicChannel) {
      if (formik.values.title && !formik.values.channelName) {
        formik.setFieldValue("channelName", formik.values.title);
      }
      if (formik.values.description && !formik.values.channelDescription) {
        formik.setFieldValue("channelDescription", formik.values.description);
      }
    }
  }, [
    formik.values.createPublicChannel,
    formik.values.title,
    formik.values.description,
    isEditing,
    formik,
  ]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formik.handleChange(e);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  const handleTrackChange = useCallback((trackId: string, checked: boolean) => {
    const currentTracks = formikRef.current.values.trackIds || [];
    let newTracks: string[];

    if (checked) {
      newTracks = [...currentTracks, trackId];
    } else {
      newTracks = currentTracks.filter((id) => id !== trackId);
    }

    formikRef.current.setFieldValue("trackIds", newTracks);
  }, []);

  const handleSpeakerChange = useCallback(
    (speakerId: string, checked: boolean) => {
      const currentSpeakers = formikRef.current.values.speakerIds || [];
      let newSpeakers: string[];

      if (checked) {
        newSpeakers = [...currentSpeakers, speakerId];
      } else {
        newSpeakers = currentSpeakers.filter((id) => id !== speakerId);
      }

      formikRef.current.setFieldValue("speakerIds", newSpeakers);
    },
    []
  );

  // Add direct array setters for better performance
  const handleTracksArrayChange = useCallback((trackIds: string[]) => {
    formikRef.current.setFieldValue("trackIds", trackIds);
  }, []);

  const handleSpeakersArrayChange = useCallback((speakerIds: string[]) => {
    formikRef.current.setFieldValue("speakerIds", speakerIds);
  }, []);

  const handleCreatePublicChannelChange = (checked: boolean) => {
    formik.setFieldValue("createPublicChannel", checked);
  };

  const handleAllowPublicViewChange = (checked: boolean) => {
    formik.setFieldValue("allowPublicView", checked);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.handleChange(e);
  };

  // Multiple files handlers
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAddFiles = (files: File[]) => {
    const newUploadedFiles: UploadedFile[] = files.map((file) => {
      const isImage = file.type.startsWith("image/");
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;

      return {
        id: `${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: formatFileSize(file.size),
        previewUrl,
      };
    });

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
    setFilesToUpload((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = async (fileId: string) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
    if (!fileToRemove) return;

    // Clean up object URL to prevent memory leaks
    if (
      fileToRemove.previewUrl &&
      fileToRemove.previewUrl.startsWith("blob:")
    ) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }

    // If it's a new file (has file property), just remove from state
    if (fileToRemove.file) {
      setFilesToUpload((prev) => prev.filter((f) => f !== fileToRemove.file));
      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
      return;
    }

    // If it's an existing file (no file property), call API to delete from backend
    try {
      const response = await connectGraphQLApi({
        query: `
          mutation BackofficeDeleteFile($fileId: ID!) {
            backofficeDeleteFile(fileId: $fileId)
          }
        `,
        variables: { fileId },
        errorMessage: "Falha ao deletar arquivo",
        useNewApi: true,
      });

      if (response.data.backofficeDeleteFile) {
        setToast({
          type: "success",
          message: "Arquivo deletado com sucesso",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        // Remove from UI only after successful deletion
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
      } else {
        setToast({
          type: "error",
          message: "Falha ao deletar arquivo",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      setToast({
        type: "error",
        message: "Erro ao deletar arquivo. Tente novamente.",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    }
  };

  // Questionnaire-related functions
  const handleFileUpload = async (file: File) => {
    setTranscriptFile(file);

    // Read file content for supported text formats
    const supportedTextFormats = [".txt", ".text"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (supportedTextFormats.includes(fileExtension)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        formik.setFieldValue("transcript", content);
      };
      reader.readAsText(file);
    } else {
      // For other formats (PDF, DOC, DOCX, audio), we would need to implement
      // server-side processing. For now, just set placeholder text.
      try {
        const text = await FileService.extractText(file);
        formik.setFieldValue("transcript", text);
      } catch (error) {
        console.error("Error extracting text from file:", error);
        setToast({
          type: "error",
          message: "Erro ao extrair texto do arquivo. Tente novamente.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      }
    }
  };

  const handleGenerateQuestionnaire = async () => {
    if (!formik.values.transcript.trim()) {
      setToast({
        type: "error",
        message: "Transcrição é obrigatória para gerar questionário",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return;
    }

    if (formik.values.transcript.length < 50) {
      setToast({
        type: "error",
        message:
          "Transcrição deve ter pelo menos 50 caracteres para gerar questionário",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return;
    }

    if (!editingSession?.sessionId) {
      setToast({
        type: "error",
        message: "Sessão deve ser salva antes de gerar questionário",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return;
    }

    setGeneratingQuestionnaire(true);

    try {
      const response = await connectGraphQLApi({
        query: `
          mutation BackofficeGenerateQuestionnaireFromTranscript($input: GenerateQuestionnaireFromTranscriptInput!) {
            backofficeGenerateQuestionnaireFromTranscript(input: $input) {
              questionnaireId
              sessionId
              name
              description
              passingScore
              status
              isPublished
              createdByUserId
              insertDate
              updateDate
              questions {
                questionId
                questionnaireId
                name
                description
                type
                options {
                  id
                  text
                  isCorrect
                  score
                  nextQuestionId
                }
                scoreWeight
                allowMultipleAnswers
                status
                nextQuestionId
                insertDate
                updateDate
              }
            }
          }
        `,
        variables: {
          input: {
            sessionId: editingSession.sessionId,
            transcript: formik.values.transcript.trim(),
          },
        },
        errorMessage: "Falha ao gerar questionário",
        useNewApi: true,
      });

      if (response.data.backofficeGenerateQuestionnaireFromTranscript) {
        onGenerateQuestionnaire?.(
          response.data.backofficeGenerateQuestionnaireFromTranscript
        );
        setToast({
          type: "success",
          message: "Questionário gerado com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    } catch (error) {
      console.error("Error generating questionnaire:", error);
      setToast({
        type: "error",
        message: "Erro ao gerar questionário. Tente novamente.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setGeneratingQuestionnaire(false);
    }
  };

  const handlePublishQuestionnaire = async () => {
    if (!editingSession?.sessionId) {
      setToast({
        type: "error",
        message: "Sessão deve ser salva antes de publicar questionário",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return;
    }

    if (!editingSession?.questionnaire?.questionnaireId) {
      setToast({
        type: "error",
        message: "Questionário deve ser gerado antes de ser publicado",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return;
    }

    if (editingSession.questionnaire.isPublished) {
      setToast({
        type: "warning",
        message: "Questionário já foi publicado",
        isActive: true,
        timeToRemoveToast: 3000,
      });
      return;
    }

    setPublishingQuestionnaire(true);

    try {
      const response = await connectGraphQLApi({
        query: `
          mutation BackofficePublishQuestionnaire($sessionId: ID!, $questionnaireId: ID!, $eventId: ID!) {
            backofficePublishQuestionnaire(sessionId: $sessionId, questionnaireId: $questionnaireId, eventId: $eventId) {
							questionnaireId
              sessionId
              name
              description
              passingScore
              status
              createdByUserId
              insertDate
              updateDate
              questions {
                questionId
                questionnaireId
                name
                description
                type
                options {
                  id
                  text
                  isCorrect
                  score
                  nextQuestionId
                }
                scoreWeight
                allowMultipleAnswers
                status
                nextQuestionId
                insertDate
                updateDate
              }
            }
          }
        `,
        variables: {
          sessionId: editingSession.sessionId,
          questionnaireId: editingSession.questionnaire.questionnaireId,
          eventId: providedEventId,
        },
        errorMessage: "Falha ao publicar questionário",
        useNewApi: true,
      });

      if (response.data.backofficePublishQuestionnaire) {
        // Update the questionnaire with published status
        const publishedQuestionnaire = {
          ...response.data.backofficePublishQuestionnaire,
          isPublished: true,
        };
        onGenerateQuestionnaire?.(publishedQuestionnaire);
        setToast({
          type: "success",
          message: "Questionário publicado com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    } catch (error) {
      console.error("Error publishing questionnaire:", error);
      setToast({
        type: "error",
        message: "Erro ao publicar questionário. Tente novamente.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setPublishingQuestionnaire(false);
    }
  };

  return {
    handleInputChange,
    handleSelectChange,
    inputsValues: formik.values,
    touched: formik.touched,
    errors: formik.errors,
    handleSubmit,
    isLoading,
    isEditing,
    // New fields for tracks, speakers and locations
    tracks,
    speakers,
    locations,
    loadingOptions,
    handleTrackChange,
    handleSpeakerChange,
    handleTracksArrayChange,
    handleSpeakersArrayChange,
    handleCreatePublicChannelChange,
    handleAllowPublicViewChange,
    // Conversation-related states
    hasExistingConversation,
    checkingConversation,
    existingConversationData,
    // Questionnaire-related exports
    transcriptFile,
    generatingQuestionnaire,
    publishingQuestionnaire,
    handleFileUpload,
    handleGenerateQuestionnaire,
    handlePublishQuestionnaire,
    // Multiple files
    uploadedFiles,
    handleAddFiles,
    handleRemoveFile,
  };
};
